import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import {
  Paperclip, Upload, FileText, Image as ImageIcon, FileType, Trash2,
  Sparkles, Loader2, AlertCircle, CheckCircle2, RefreshCcw, X,
  FileAudio, XCircle, FolderInput, ExternalLink, Pencil, Check, ChevronDown, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import { MaterialPreviewModal } from "@/components/MaterialPreviewModal";
import { MaterialAudioControls } from "@/components/MaterialAudioControls";

const ICON_BY_TYPE = (ct = "") => {
  if (ct.includes("pdf")) return FileText;
  if (ct.startsWith("image/")) return ImageIcon;
  return FileType;
};

const STATUS_LABEL = {
  ANALYZING: { txt: "Edit läser…", Icon: Loader2, anim: true, color: "var(--jarvis)" },
  READY: { txt: "Klart", Icon: CheckCircle2, anim: false, color: "var(--success)" },
  ANALYSIS_FAILED: { txt: "Analys misslyckades", Icon: AlertCircle, anim: false, color: "var(--danger)" },
  UPLOAD_FAILED: { txt: "Uppladdning misslyckades", Icon: AlertCircle, anim: false, color: "var(--danger)" },
};

const formatSize = (b) => {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} kB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * Visar materialets sammanfattning i två lägen:
 * - Kollapserat: bara den korta `preview` (200 tecken från backend).
 * - Expanderat: hela `full` med scrollbar om den är mycket lång.
 *
 * Knappen visas bara om `full` är längre än `preview` (annars finns inget
 * att expandera till).
 */
function ExpandableSummary({ preview, full, materialId }) {
  const [expanded, setExpanded] = useState(false);
  // Om backend inte gav någon preview, generera en kort version av full
  // (300 tecken klipp på ord-gränsen) så vi alltid har något att visa
  // i kollapsat läge. Annars ser kortet tomt ut + en ensam liten länk.
  const safeFull = (full || "").trim();
  const safePreview = (preview || "").trim();
  const fallbackPreview = (() => {
    if (safePreview) return safePreview;
    if (safeFull.length <= 300) return safeFull;
    const cut = safeFull.slice(0, 300);
    const lastSpace = cut.lastIndexOf(" ");
    return (lastSpace > 200 ? cut.slice(0, lastSpace) : cut) + "…";
  })();
  const canExpand = safeFull.length > fallbackPreview.length + 5;
  const text = expanded && canExpand ? safeFull : fallbackPreview;
  if (!text) return null;
  return (
    <div className="mt-2">
      <p
        className={`text-xs text-[color:var(--text-secondary)] leading-relaxed whitespace-pre-wrap ${
          expanded ? "max-h-64 overflow-y-auto pr-1" : ""
        }`}
        data-testid={`material-summary-${materialId}`}
      >
        {text}
      </p>
      {canExpand && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          className="mt-1 text-[10px] inline-flex items-center gap-0.5 text-[color:var(--jarvis)] hover:underline"
          data-testid={`material-summary-toggle-${materialId}`}
        >
          {expanded ? (
            <>
              <ChevronUp size={11} /> Visa mindre
            </>
          ) : (
            <>
              <ChevronDown size={11} /> Visa hela sammanfattningen
            </>
          )}
        </button>
      )}
    </div>
  );
}

export function MaterialUploader({ eventId, eventCategory, onAnalysisReady }) {
  const [open, setOpen] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  // ZIP-audio-batch state
  const [audioJob, setAudioJob] = useState(null);
  const [audioLang, setAudioLang] = useState("sv");
  const [audioAttached, setAudioAttached] = useState(false);
  const fileRef = useRef(null);
  const zipRef = useRef(null);
  const pollRef = useRef(null);
  const audioPollRef = useRef(null);
  // Move-material state
  const [moveOpenId, setMoveOpenId] = useState(null);
  const [moveTargets, setMoveTargets] = useState(null);
  const [moveBusy, setMoveBusy] = useState(false);
  // URL-material state
  const [urlInput, setUrlInput] = useState("");
  const [urlAdding, setUrlAdding] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const [reanalyzingAll, setReanalyzingAll] = useState(false);
  // Rename-state
  const [renameId, setRenameId] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const loadMaterials = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/materials/event/${encodeURIComponent(eventId)}`);
      setMaterials(r.data.materials || []);
    } catch (e) {
      console.warn("loadMaterials fel", e);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  // Auto-ladda materialstatus så fort knappen monteras (för räknare/badge),
  // OCH varje gång modalen öppnas (för uppdaterad lista).
  useEffect(() => {
    if (eventId) loadMaterials();
  }, [eventId, loadMaterials]);

  // Polla pågående analyser — kör även när modalen är stängd så badge uppdateras
  useEffect(() => {
    const hasPending = materials.some((m) => m.status === "ANALYZING");
    if (!hasPending) return;
    pollRef.current = setInterval(loadMaterials, 4000);
    return () => clearInterval(pollRef.current);
  }, [materials, loadMaterials]);

  // Notifiera parent när någon analys blir READY (för auto-uppdatering av prep-plan)
  const readyCount = materials.filter((m) => m.status === "READY").length;
  useEffect(() => {
    if (readyCount > 0 && onAnalysisReady) onAnalysisReady(readyCount);
  }, [readyCount, onAnalysisReady]);

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 25 * 1024 * 1024) {
          toast.error(`${file.name} är för stor (max 25 MB)`);
          continue;
        }
        const fd = new FormData();
        fd.append("event_id", eventId);
        fd.append("file", file);
        try {
          await api.post("/materials/upload", fd);
          toast.success(`Laddade upp ${file.name} — Edit analyserar…`);
        } catch (e) {
          toast.error(`Misslyckades: ${file.name}`);
          console.warn(e);
        }
      }
      await loadMaterials();
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files || []);
    if (!files.length) return;
    // Om en ZIP släpps in → starta ljudbatch istället för vanlig uppladdning
    const zip = files.find((f) => f.name.toLowerCase().endsWith(".zip"));
    if (zip) {
      handleZipUpload(zip);
      return;
    }
    handleFiles(files);
  };

  // ----------------------------------------------------------------------
  // ZIP audio batch — transkribera mp3/m4a/wav... i ordning och bifoga som
  // inline-textmaterial till eventet så Edit kan generera quiz på det.
  // ----------------------------------------------------------------------
  const stopAudioPolling = () => {
    if (audioPollRef.current) {
      clearInterval(audioPollRef.current);
      audioPollRef.current = null;
    }
  };

  const attachAudioJobToEvent = async (jobId) => {
    try {
      await api.post(`/audio-batch/${jobId}/attach-to-event/${encodeURIComponent(eventId)}`);
      setAudioAttached(true);
      toast.success("Transkript bifogat — Edit analyserar nu");
      await loadMaterials();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte bifoga transkriptet");
    }
  };

  const pollAudioJob = (jobId) => {
    stopAudioPolling();
    audioPollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/audio-batch/${jobId}`);
        setAudioJob(data);
        if (["done", "partial", "failed"].includes(data.status)) {
          stopAudioPolling();
          // Backend gör auto-attach själv (vi skickade auto_attach_event_id vid upload).
          // Vi kollar bara om backenden flaggat det som "done" och uppdaterar UI.
          if (data.auto_attach_status === "done") {
            setAudioAttached(true);
            await loadMaterials();
          } else if (["done", "partial"].includes(data.status) && (data.combined_transcript || "").trim()) {
            // Fallback: om auto-attach inte triggades (gamla job utan event_id) — gör det här
            await attachAudioJobToEvent(jobId);
          }
        }
      } catch {
        // soft fail
      }
    }, 2500);
  };

  const handleZipUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".zip")) {
      toast.error("Välj en .zip-fil");
      return;
    }
    if (file.size > 200 * 1024 * 1024) {
      toast.error(`ZIP för stor (max 200 MB, fick ${(file.size / 1024 / 1024).toFixed(0)} MB)`);
      return;
    }
    setUploading(true);
    setAudioAttached(false);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("language", audioLang);
      // Skicka med event_id så backend auto-bifogar transkriptet när jobbet är klart,
      // även om användaren stänger modalen i mellantiden. Transkriptet hamnar då
      // som material på rätt event utan att kräva att frontend är öppen.
      if (eventId) {
        fd.append("auto_attach_event_id", eventId);
      }
      const { data } = await api.post("/audio-batch/upload", fd);
      setAudioJob({ ...data, completed: 0, failed: 0 });
      toast.success(`${data.total} ljudfiler i kö — du kan stänga modalen, jobbet körs i bakgrunden`);
      pollAudioJob(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "ZIP-uppladdning misslyckades");
    } finally {
      setUploading(false);
      if (zipRef.current) zipRef.current.value = "";
    }
  };

  useEffect(() => () => stopAudioPolling(), []);

  const handleReanalyze = async (id) => {
    try {
      await api.post(`/materials/${id}/reanalyze`);
      toast.success("Edit analyserar om…");
      await loadMaterials();
    } catch {
      toast.error("Kunde inte triggera analys");
    }
  };

  /** Bulk-reanalysera ALLA misslyckade material för detta event/hemarbete.
   * Triggas från "Reta-igen-alla"-knappen i listans header när det finns
   * minst 1 ANALYSIS_FAILED/UPLOAD_FAILED. */
  const handleReanalyzeAllFailed = async () => {
    const failed = materials.filter((m) =>
      ["ANALYSIS_FAILED", "UPLOAD_FAILED"].includes(m.status),
    );
    if (failed.length === 0) return;
    setReanalyzingAll(true);
    let ok = 0;
    let fail = 0;
    for (const m of failed) {
      try {
        await api.post(`/materials/${m.id}/reanalyze`);
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setReanalyzingAll(false);
    if (fail === 0) toast.success(`${ok} material analyseras om`);
    else toast.warning(`${ok} OK, ${fail} misslyckades`);
    await loadMaterials();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Ta bort detta material?")) return;
    try {
      await api.delete(`/materials/${id}`);
      toast.success("Material borttaget");
      await loadMaterials();
    } catch {
      toast.error("Kunde inte ta bort");
    }
  };

  const startRename = (m) => {
    setRenameId(m.id);
    setRenameValue(m.filename || "");
  };

  const cancelRename = () => {
    setRenameId(null);
    setRenameValue("");
  };

  const submitRename = async () => {
    const value = renameValue.trim();
    if (!value) {
      toast.error("Filnamn får inte vara tomt");
      return;
    }
    try {
      await api.patch(`/materials/${renameId}`, { filename: value });
      toast.success("Filnamn uppdaterat");
      setRenameId(null);
      setRenameValue("");
      await loadMaterials();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte uppdatera namn");
    }
  };

  // Flytta material till annan läxa/prov (cross-child stöds — admin kan flytta
  // mellan barnens events). Backend rensar explain/prep-plan-cache automatiskt.
  const openMovePopover = async (materialId) => {
    setMoveOpenId(materialId);
    if (moveTargets) return; // redan laddat
    try {
      const { data } = await api.get("/materials/move-targets");
      setMoveTargets(data.events || []);
    } catch (e) {
      toast.error("Kunde inte hämta målmål");
      setMoveOpenId(null);
    }
  };

  const handleMove = async (materialId, targetEventId, targetSummary) => {
    setMoveBusy(true);
    try {
      await api.post(`/materials/${materialId}/move-to-event/${encodeURIComponent(targetEventId)}`);
      toast.success(`Flyttat till "${targetSummary}"`);
      setMoveOpenId(null);
      await loadMaterials();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Flytt misslyckades");
    } finally {
      setMoveBusy(false);
    }
  };

  const handleUrlAdd = async () => {
    const url = urlInput.trim();
    if (!url) return;
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      toast.error("URL måste börja med http:// eller https://");
      return;
    }
    setUrlAdding(true);
    try {
      const fd = new FormData();
      fd.append("event_id", eventId);
      fd.append("url", url);
      const { data } = await api.post("/materials/upload-url", fd);
      toast.success(`Länk sparad: "${data?.material?.filename || "Pluggresurs"}"`);
      setUrlInput("");
      await loadMaterials();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte spara länken");
    } finally {
      setUrlAdding(false);
    }
  };

  // handleSpeak ersatt av <MaterialAudioControls> per-material —
  // läggs ut i listan tillsammans med övriga åtgärdsknappar.


  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full flex items-center justify-center gap-2 text-sm font-medium px-4 py-3 rounded-xl border border-[color:var(--border)] bg-[color:var(--bg-elevated)] hover:bg-[color:var(--bg-soft)] transition-colors"
        data-testid="material-upload-btn"
      >
        <Paperclip size={16} className="text-[color:var(--jarvis)]" />
        <span>
          Material {materials.length > 0 ? `(${materials.length})` : ""}
          {readyCount > 0 && (
            <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[color:var(--jarvis)] text-white">
              {readyCount} analyserat
            </span>
          )}
        </span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center"
          onClick={() => setOpen(false)}
          data-testid="material-uploader-backdrop"
        >
          <div
            className="bg-[color:var(--bg-surface)] w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="material-uploader-modal"
          >
            <div className="flex items-center justify-between p-4 border-b border-[color:var(--border)]">
              <div className="flex items-center gap-2">
                <Paperclip size={18} className="text-[color:var(--jarvis)]" />
                <h3 className="font-semibold">
                  Material för {eventCategory === "test" ? "provet" : "läxan"}
                </h3>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-lg hover:bg-[color:var(--bg-elevated)]"
                data-testid="material-uploader-close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {/* Drop-zone */}
              <div
                onDrop={onDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-[color:var(--border)] rounded-xl p-6 text-center cursor-pointer hover:bg-[color:var(--bg-elevated)] transition-colors"
                data-testid="material-drop-zone"
              >
                <Upload size={28} className="mx-auto text-[color:var(--jarvis)] mb-2" />
                <p className="text-sm font-medium">Klicka eller dra hit filer</p>
                <p className="text-xs text-[color:var(--text-tertiary)] mt-1">
                  PDF, bilder, Word eller text (max 25 MB)
                </p>
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  accept=".pdf,.docx,.txt,.md,.csv,image/*"
                  onChange={(e) => handleFiles(Array.from(e.target.files || []))}
                  className="hidden"
                  data-testid="material-file-input"
                />
              </div>

              {/* URL-material — spara en länk (Wikipedia, SO-rummet, NE.se, riksdagen.se etc)
                  så Edit kan läsa in pluggresursen och använda i förklaring/quiz. */}
              <div className="border border-[color:var(--border)] rounded-xl p-3" data-testid="url-material-section">
                <div className="flex items-center gap-2 mb-1.5">
                  <ExternalLink size={14} className="text-[color:var(--jarvis)]" />
                  <p className="text-sm font-medium">Spara länk som material</p>
                </div>
                <p className="text-[11px] text-[color:var(--text-tertiary)] mb-2 leading-snug">
                  Edit hämtar artikelns text och använder den för förklaring + quiz.
                </p>
                <div className="flex gap-1.5">
                  <input
                    type="url"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleUrlAdd(); }}
                    placeholder="https://so-rummet.se/..."
                    disabled={urlAdding}
                    className="flex-1 text-xs border border-[color:var(--border)] rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-[color:var(--jarvis)] disabled:opacity-50"
                    data-testid="url-material-input"
                  />
                  <button
                    type="button"
                    onClick={handleUrlAdd}
                    disabled={urlAdding || !urlInput.trim()}
                    className="text-xs font-medium px-3 py-1.5 rounded-lg bg-[color:var(--jarvis)] text-white disabled:opacity-40 flex items-center gap-1"
                    data-testid="url-material-add"
                  >
                    {urlAdding ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
                    {urlAdding ? "Hämtar…" : "Lägg till"}
                  </button>
                </div>
              </div>

              {uploading && (
                <div className="flex items-center justify-center gap-2 py-2 text-sm text-[color:var(--text-tertiary)]">
                  <Loader2 size={14} className="animate-spin" />
                  Laddar upp…
                </div>
              )}

              {/* ZIP audio batch — endast på läxa/prov */}
              {["homework", "test"].includes(eventCategory) && (
                <div className="border border-[color:var(--border)] rounded-xl p-3" data-testid="audio-batch-section">
                  <div className="flex items-center gap-2 mb-2">
                    <FileAudio size={16} className="text-[color:var(--jarvis)]" />
                    <p className="text-sm font-medium">Ljud-ZIP (mp3/m4a/wav…)</p>
                  </div>
                  {!audioJob && (
                    <>
                      <p className="text-[11px] text-[color:var(--text-tertiary)] mb-2 leading-snug">
                        Packa flera ljudfiler (t.ex. inspelade lektioner) i en ZIP. Whisper transkriberar dem i ordning och bifogar texten som material — Edit kan sedan generera quiz från innehållet.
                      </p>
                      <div className="flex items-center gap-2 mb-2">
                        <label className="text-[11px] text-[color:var(--text-secondary)]">Språk:</label>
                        <select
                          value={audioLang}
                          onChange={(e) => setAudioLang(e.target.value)}
                          className="text-[11px] border border-[color:var(--border)] rounded-md px-1.5 py-1 bg-white"
                          data-testid="audio-batch-lang-select"
                        >
                          <option value="sv">Svenska</option>
                          <option value="en">Engelska</option>
                          <option value="da">Danska</option>
                          <option value="no">Norska</option>
                          <option value="de">Tyska</option>
                          <option value="fr">Franska</option>
                          <option value="es">Spanska</option>
                        </select>
                      </div>
                      <input
                        ref={zipRef}
                        type="file"
                        accept=".zip,application/zip,application/x-zip-compressed"
                        className="hidden"
                        onChange={(e) => handleZipUpload(e.target.files?.[0])}
                        data-testid="audio-batch-zip-input"
                      />
                      <button
                        type="button"
                        onClick={() => zipRef.current?.click()}
                        disabled={uploading}
                        className="w-full text-xs font-medium px-3 py-2 rounded-lg bg-[color:var(--jarvis)] text-white flex items-center justify-center gap-1.5 disabled:opacity-50"
                        data-testid="audio-batch-zip-btn"
                      >
                        <Upload size={12} /> Välj ZIP-fil (max 200 MB)
                      </button>
                    </>
                  )}
                  {audioJob && (
                    <div data-testid="audio-batch-progress">
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-xs font-medium">
                          {["done", "partial", "failed"].includes(audioJob.status)
                            ? audioJob.status === "done"
                              ? "Transkribering klar"
                              : audioJob.status === "partial"
                                ? "Delvis klar"
                                : "Misslyckades"
                            : "Transkriberar…"}
                        </p>
                        <span className="text-[10px] text-[color:var(--text-tertiary)]">
                          {(audioJob.completed || 0) + (audioJob.failed || 0)} / {audioJob.total}
                        </span>
                      </div>
                      <div className="w-full h-1.5 bg-[color:var(--bg-elevated)] rounded-full overflow-hidden mb-2">
                        <div
                          className="h-full bg-[color:var(--jarvis)] transition-all"
                          style={{
                            width: `${Math.round((((audioJob.completed || 0) + (audioJob.failed || 0)) / Math.max(1, audioJob.total)) * 100)}%`,
                          }}
                        />
                      </div>
                      <div className="space-y-1 max-h-44 overflow-y-auto pr-1">
                        {(audioJob.files || []).map((f) => (
                          <div
                            key={f.order}
                            className="flex items-center gap-1.5 text-[11px]"
                            data-testid={`audio-batch-file-${f.order}`}
                          >
                            <span className="w-4 flex-shrink-0">
                              {f.status === "done" && <CheckCircle2 size={11} className="text-[color:var(--success)]" />}
                              {f.status === "failed" && <XCircle size={11} className="text-[color:var(--danger)]" />}
                              {f.status === "running" && <Loader2 size={11} className="animate-spin text-[color:var(--jarvis)]" />}
                              {f.status === "queued" && <FileAudio size={11} className="text-[color:var(--text-tertiary)]" />}
                            </span>
                            <span className="text-[color:var(--text-tertiary)] w-4 flex-shrink-0">{f.order + 1}.</span>
                            <span className="flex-1 truncate" title={f.name}>{f.name}</span>
                            {f.error && (
                              <span className="text-[9px] text-[color:var(--danger)] truncate max-w-[40%]" title={f.error}>
                                fel
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                      {["done", "partial", "failed"].includes(audioJob.status) && (
                        <div className="mt-2 flex items-center justify-between gap-2">
                          {audioAttached ? (
                            <span className="text-[10px] text-[color:var(--success)] flex items-center gap-1">
                              <CheckCircle2 size={10} /> Bifogat som material
                            </span>
                          ) : (
                            <span className="text-[10px] text-[color:var(--text-tertiary)]">
                              {audioJob.status === "failed" ? "Inget att bifoga" : "Bifogar…"}
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => { setAudioJob(null); setAudioAttached(false); stopAudioPolling(); }}
                            className="text-[10px] font-medium px-2 py-1 rounded-md border border-[color:var(--border)]"
                            data-testid="audio-batch-reset"
                          >
                            Ny ZIP
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Material-lista */}
              {loading && !materials.length ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 size={18} className="animate-spin text-[color:var(--text-tertiary)]" />
                </div>
              ) : materials.length === 0 ? (
                <p className="text-center text-sm text-[color:var(--text-tertiary)] py-6">
                  Inga material uppladdade än. Lägg till PDF, bilder eller dokument så analyserar Edit dem.
                </p>
              ) : (
                <div className="space-y-2">
                  {/* Bulk-retry-banner — visas bara när ≥1 material har FAILED.
                      Snabbväg så förälder slipper klicka individuellt på varje rad. */}
                  {(() => {
                    const failedCount = materials.filter((m) =>
                      ["ANALYSIS_FAILED", "UPLOAD_FAILED"].includes(m.status),
                    ).length;
                    if (failedCount === 0) return null;
                    return (
                      <div
                        className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl bg-[#FEF2F2] border border-[#FECACA]"
                        data-testid="material-bulk-retry-banner"
                      >
                        <p className="text-xs text-[color:var(--text-primary)] flex items-center gap-1.5 min-w-0">
                          <AlertCircle size={13} className="text-[color:var(--danger)] flex-shrink-0" />
                          <span className="font-medium">
                            {failedCount} material misslyckades att analyseras
                          </span>
                        </p>
                        <button
                          onClick={handleReanalyzeAllFailed}
                          disabled={reanalyzingAll}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[color:var(--jarvis)] text-white text-[11px] font-medium hover:opacity-90 disabled:opacity-50 flex-shrink-0"
                          data-testid="material-bulk-retry-btn"
                        >
                          {reanalyzingAll ? (
                            <Loader2 size={11} className="animate-spin" />
                          ) : (
                            <RefreshCcw size={11} />
                          )}
                          {reanalyzingAll ? "Triggar…" : "Försök analysera alla igen"}
                        </button>
                      </div>
                    );
                  })()}
                  {materials.map((m) => {
                    const Icon = ICON_BY_TYPE(m.content_type || "");
                    const sm = STATUS_LABEL[m.status] || {};
                    const SIcon = sm.Icon;
                    return (
                      <div
                        key={m.id}
                        className="card-soft p-3"
                        data-testid={`material-item-${m.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <Icon size={20} className="text-[color:var(--text-tertiary)] flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            {renameId === m.id ? (
                              <div className="flex items-center gap-1.5">
                                <input
                                  type="text"
                                  value={renameValue}
                                  onChange={(e) => setRenameValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") submitRename();
                                    if (e.key === "Escape") cancelRename();
                                  }}
                                  className="flex-1 text-sm font-medium px-2 py-1 rounded border border-[color:var(--border)] focus:outline-none focus:ring-2 focus:ring-[color:var(--jarvis)]"
                                  autoFocus
                                  maxLength={160}
                                  data-testid={`material-rename-input-${m.id}`}
                                />
                                <button
                                  type="button"
                                  onClick={submitRename}
                                  className="p-1 rounded hover:bg-[color:var(--bg-elevated)]"
                                  title="Spara"
                                  data-testid={`material-rename-save-${m.id}`}
                                >
                                  <Check size={14} className="text-[color:var(--success)]" />
                                </button>
                                <button
                                  type="button"
                                  onClick={cancelRename}
                                  className="p-1 rounded hover:bg-[color:var(--bg-elevated)]"
                                  title="Avbryt"
                                  data-testid={`material-rename-cancel-${m.id}`}
                                >
                                  <X size={14} className="text-[color:var(--text-tertiary)]" />
                                </button>
                              </div>
                            ) : m.commhub_file_id ? (
                              <button
                                type="button"
                                onClick={() => setPreviewId(m.id)}
                                className="text-sm font-medium truncate text-left hover:underline hover:text-[color:var(--jarvis)] w-full"
                                title={`Öppna ${m.filename}`}
                                data-testid={`material-open-${m.id}`}
                              >
                                {m.filename}
                              </button>
                            ) : m.source_kind === "youtube" ? (
                              <button
                                type="button"
                                onClick={() => setPreviewId(m.id)}
                                className="text-sm font-medium truncate text-left hover:underline hover:text-[color:var(--jarvis)] w-full inline-flex items-center gap-1.5"
                                title={`Spela ${m.filename}`}
                                data-testid={`material-open-${m.id}`}
                              >
                                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#DC2626] text-white text-[8px]">▶</span>
                                {m.filename}
                              </button>
                            ) : m.url || m.source_url ? (
                              <a
                                href={m.url || m.source_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm font-medium truncate hover:underline hover:text-[color:var(--jarvis)] block"
                                title={`Öppna ${m.filename}`}
                              >
                                {m.filename}
                              </a>
                            ) : (
                              <p className="text-sm font-medium truncate" title={m.filename}>
                                {m.filename}
                              </p>
                            )}
                            <div className="flex items-center gap-2 mt-0.5 text-xs text-[color:var(--text-tertiary)]">
                              <span>{formatSize(m.size_bytes)}</span>
                              {SIcon && (
                                <span
                                  className="flex items-center gap-1"
                                  style={{ color: sm.color }}
                                >
                                  <SIcon size={11} className={sm.anim ? "animate-spin" : ""} />
                                  {sm.txt}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {m.status === "READY" && m.source_kind !== "youtube" && (
                              <MaterialAudioControls
                                materialId={m.id}
                                filename={m.filename}
                                compact
                              />
                            )}
                            {["READY", "ANALYSIS_FAILED", "UPLOAD_FAILED"].includes(m.status) && (
                              <button
                                onClick={() => handleReanalyze(m.id)}
                                className="p-1.5 rounded hover:bg-[color:var(--bg-elevated)]"
                                title={m.status === "READY" ? "Analysera om" : "Försök igen"}
                                data-testid={`material-reanalyze-${m.id}`}
                              >
                                <RefreshCcw size={14} className={m.status !== "READY" ? "text-[color:var(--jarvis)]" : "text-[color:var(--text-tertiary)]"} />
                              </button>
                            )}
                            <button
                              onClick={() => openMovePopover(m.id)}
                              className="p-1.5 rounded hover:bg-[color:var(--bg-elevated)]"
                              title="Flytta till annan läxa/prov"
                              data-testid={`material-move-${m.id}`}
                            >
                              <FolderInput size={14} className="text-[color:var(--text-tertiary)]" />
                            </button>
                            <button
                              onClick={() => startRename(m)}
                              className="p-1.5 rounded hover:bg-[color:var(--bg-elevated)]"
                              title="Redigera filnamn"
                              data-testid={`material-rename-${m.id}`}
                            >
                              <Pencil size={14} className="text-[color:var(--text-tertiary)]" />
                            </button>
                            <button
                              onClick={() => handleDelete(m.id)}
                              className="p-1.5 rounded hover:bg-[color:var(--bg-elevated)]"
                              title="Ta bort"
                              data-testid={`material-delete-${m.id}`}
                            >
                              <Trash2 size={14} className="text-[color:var(--danger)]" />
                            </button>
                          </div>
                        </div>
                        {/* Sammanfattning + quiz-info i full bredd UNDER rad-headern.
                           Annars klämmer ikonerna till höger ihop texten på smala
                           mobilskärmar (issue 2026-05-31). */}
                        {(m.summary_preview || m.analysis?.summary) && (
                          <ExpandableSummary
                            preview={m.summary_preview}
                            full={m.analysis?.summary || m.summary_preview}
                            materialId={m.id}
                          />
                        )}
                        {m.analysis?.quiz_questions?.length > 0 && (
                          <p className="text-[10px] text-[color:var(--jarvis)] mt-2 flex items-center gap-1">
                            <Sparkles size={10} />
                            {m.analysis.quiz_questions.length} quiz-frågor klara
                            {m.analysis.estimated_study_min ? ` · ${m.analysis.estimated_study_min} min pluggtid` : ""}
                          </p>
                        )}
                        {m.error && ["ANALYSIS_FAILED", "UPLOAD_FAILED"].includes(m.status) && (
                          <>
                            <p className="text-[10px] text-[color:var(--danger)] mt-2 flex items-start gap-1 leading-relaxed" data-testid={`material-error-${m.id}`}>
                              <AlertCircle size={11} className="flex-shrink-0 mt-0.5" />
                              <span className="break-words">{m.error}</span>
                            </p>
                            <button
                              onClick={() => handleReanalyze(m.id)}
                              className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[color:var(--jarvis)] text-white text-[11px] font-medium hover:opacity-90"
                              data-testid={`material-retry-cta-${m.id}`}
                            >
                              <RefreshCcw size={11} />
                              Försök analysera igen
                            </button>
                          </>
                        )}
                        {/* Flytta-popover: lista över andra läxor/prov (alla barn) */}
                        {moveOpenId === m.id && (
                          <div
                            className="mt-2 border border-[color:var(--border)] rounded-xl bg-white shadow-sm"
                            data-testid={`material-move-panel-${m.id}`}
                          >
                            <div className="flex items-center justify-between px-3 py-2 border-b border-[color:var(--border)]">
                              <p className="text-[11px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)]">
                                Flytta material till…
                              </p>
                              <button
                                onClick={() => setMoveOpenId(null)}
                                className="text-[color:var(--text-tertiary)] hover:text-[color:var(--text-primary)]"
                                data-testid="material-move-cancel"
                              >
                                <X size={12} />
                              </button>
                            </div>
                            {!moveTargets ? (
                              <p className="px-3 py-3 text-[11px] text-[color:var(--text-tertiary)] flex items-center gap-1.5">
                                <Loader2 size={11} className="animate-spin" /> Laddar händelser…
                              </p>
                            ) : moveTargets.length === 0 ? (
                              <p className="px-3 py-3 text-[11px] text-[color:var(--text-tertiary)] italic">
                                Inga andra läxor/prov tillgängliga.
                              </p>
                            ) : (
                              <ul className="max-h-64 overflow-y-auto py-1">
                                {moveTargets
                                  .filter((t) => t.id !== eventId)
                                  .map((t) => (
                                    <li key={t.id}>
                                      <button
                                        onClick={() => handleMove(m.id, t.id, t.summary)}
                                        disabled={moveBusy}
                                        className="w-full text-left px-3 py-2 hover:bg-[color:var(--bg-elevated)] disabled:opacity-50"
                                        data-testid={`material-move-target-${t.id}`}
                                      >
                                        <div className="flex items-center gap-2 text-xs">
                                          <span
                                            className={`text-[9px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide flex-shrink-0 ${
                                              t.category === "test"
                                                ? "bg-[#FDF2EE] text-[#E07A5F]"
                                                : t.category === "assignment"
                                                ? "bg-[#E7F4F1] text-[#2A9D8F]"
                                                : "bg-[#F0F6E6] text-[color:var(--moss)]"
                                            }`}
                                          >
                                            {t.category === "test" ? "Prov" : t.category === "assignment" ? "Uppgift" : "Läxa"}
                                          </span>
                                          <span className="flex-1 truncate font-medium">{t.summary}</span>
                                        </div>
                                        <p className="text-[10px] text-[color:var(--text-tertiary)] mt-0.5 ml-12">
                                          {t.owner} · {(t.start || "").slice(0, 10)}
                                        </p>
                                      </button>
                                    </li>
                                  ))}
                              </ul>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <MaterialPreviewModal
        materialId={previewId}
        open={!!previewId}
        onClose={() => setPreviewId(null)}
      />
    </>
  );
}
